import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Building2, ChevronUp, ChevronDown, Eye, EyeOff, AlertTriangle } from 'lucide-react';

const COLORS = [
  '#6366F1','#8B5CF6','#EC4899','#EF4444','#F97316',
  '#F59E0B','#EAB308','#84CC16','#10B981','#14B8A6',
  '#06B6D4','#3B82F6','#A855F7','#64748B','#78716C'
];

export default function DepartmentsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('departments');
  const [depts, setDepts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#3B82F6' });
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get('/departments');
    const sorted = [...r.data].sort((a, b) => (a.stage_order || 999) - (b.stage_order || 999));
    setDepts(sorted);
  };
  useEffect(() => { load(); }, []);

  const activeDepts = depts.filter(d => d.is_active !== 0);
  const inactiveDepts = depts.filter(d => d.is_active === 0);
  const displayDepts = showInactive ? depts : activeDepts;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    setLoading(true);
    try {
      if (editDept) {
        await api.put(`/departments/${editDept.id}`, { ...form, is_active: editDept.is_active ?? 1 });
        toast.success('Department updated');
      } else {
        await api.post('/departments', form);
        toast.success('Department created');
      }
      setShowModal(false); setEditDept(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const handleToggleActive = async (d) => {
    try {
      await api.put(`/departments/${d.id}`, { name: d.name, description: d.description, color: d.color, is_active: d.is_active ? 0 : 1 });
      toast.success(d.is_active ? 'Deactivated' : 'Activated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (d, force = false) => {
    setLoading(true);
    try {
      if (force) {
        await api.delete(`/departments/${d.id}/force`);
        toast.success('Department permanently deleted ✅');
      } else {
        await api.delete(`/departments/${d.id}`);
        toast.success('Department deactivated ✅');
      }
      setDeleteTarget(null); load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Delete failed';
      toast.error(msg, { duration: 5000 });
      // If there are active tasks, show force delete option becomes visible
      console.error('Dept delete error:', msg);
    }
    finally { setLoading(false); }
  };

  const reorder = async (activeList) => {
    const order = activeList.map((d, i) => ({ id: d.id, stage_order: i + 1 }));
    try { await api.post('/departments/reorder', { order }); load(); }
    catch { toast.error('Reorder failed'); }
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const list = [...activeDepts];
    [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
    reorder(list);
  };

  const moveDown = (idx) => {
    if (idx >= activeDepts.length - 1) return;
    const list = [...activeDepts];
    [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
    reorder(list);
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
          <p className="text-gray-500 text-sm mt-0.5">{activeDepts.length} active · {inactiveDepts.length} inactive</p>
        </div>
        <div className="flex gap-2">
          {inactiveDepts.length > 0 && (
            <button onClick={() => setShowInactive(!showInactive)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all ${showInactive ? 'border-gray-400 bg-gray-100 text-gray-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {showInactive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showInactive ? 'Hide Inactive' : `Show Inactive (${inactiveDepts.length})`}
            </button>
          )}
          {canWrite && <button onClick={() => { setEditDept(null); setForm({ name: '', description: '', color: COLORS[Math.floor(Math.random() * COLORS.length)] }); setShowModal(true); }}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold">
            <Plus className="w-4 h-4" /> Add Department
          </button>}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          Numbers = default stage order — used in production chains. Use up/down arrows to reorder.
        </p>
      </div>

      <div className="space-y-2">
        {displayDepts.map((d) => {
          const isInactive = !d.is_active;
          const activeIdx = activeDepts.findIndex(x => x.id === d.id);
          return (
            <div key={d.id} className={`bg-white rounded-2xl shadow-sm border flex items-center gap-3 p-4 transition-all ${isInactive ? 'opacity-50 border-gray-100' : 'border-gray-100 hover:shadow-md'}`}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm text-white flex-shrink-0" style={{ backgroundColor: d.color }}>
                {d.stage_order || '—'}
              </div>
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900">{d.name}</p>
                  {isInactive && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inactive</span>}
                </div>
                {d.description && <p className="text-xs text-gray-400">{d.description}</p>}
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs text-gray-500 mr-2">
                <div className="text-center"><p className="font-semibold text-gray-700">{d.worker_count || 0}</p><p>Workers</p></div>
                <div className="text-center"><p className="font-semibold text-blue-600">{d.active_tasks || 0}</p><p>Active</p></div>
                <div className="text-center"><p className="font-semibold text-green-600">{d.completed_tasks || 0}</p><p>Done</p></div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!isInactive && (
                  <div className="flex flex-col gap-0.5 mr-1">
                    <button onClick={() => moveUp(activeIdx)} disabled={activeIdx === 0} className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveDown(activeIdx)} disabled={activeIdx >= activeDepts.length - 1} className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                {canEdit && <button onClick={() => { setEditDept(d); setForm({ name: d.name, description: d.description || '', color: d.color }); setShowModal(true); }} className="p-2 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                <button onClick={() => handleToggleActive(d)} title={d.is_active ? 'Deactivate' : 'Activate'} className={`p-2 rounded-lg ${d.is_active ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-green-50 text-green-500'}`}>{d.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                {canDelete && <button onClick={() => setDeleteTarget(d)} className="p-2 rounded-lg hover:bg-red-50 text-red-400"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          );
        })}
      </div>

      {displayDepts.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No departments yet</p>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480}}>
            <div className="modal-header">
              <h2 className="text-lg font-bold">{editDept ? 'Edit Department' : 'New Department'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body space-y-4">
              <div>
                <label className="field-label">Department Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="field-input"
                  placeholder="e.g. Cutting, Assembling, Painting" />
              </div>
              <div>
                <label className="field-label">Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="field-input" placeholder="Optional" />
              </div>
              <div>
                <label className="field-label">Color</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    className="w-10 h-8 rounded cursor-pointer border border-gray-200" />
                  <span className="text-xs text-gray-400">Custom color</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={loading} className="btn-primary" style={{flex:1}}>
                  {loading ? 'Saving...' : editDept ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="confirm-box">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Delete "{deleteTarget.name}"?</p>
                <p className="text-xs text-gray-500 mt-0.5">Tasks and data may be affected</p>
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-orange-700">
                <strong>Deactivate (Safe):</strong> Department will be hidden, data stays safe. Will not work if there are active tasks.<br />
                <strong>Permanently Delete:</strong> All linked data (tasks, workers) will also be removed — only do this when sure and no active work remains.
              </p>
            </div>
            {deleteTarget?.active_tasks > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-xs text-red-700 font-semibold">
                  ⚠️ This department has {deleteTarget.active_tasks} active task(s)!
                  Please go to the Tasks page and complete them before deactivating.
                </p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button onClick={() => handleDelete(deleteTarget, false)} disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl text-sm font-semibold">Deactivate (Safe)</button>
              <button onClick={() => handleDelete(deleteTarget, true)} disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-semibold">Delete Permanently ⚠️</button>
              <button onClick={() => setDeleteTarget(null)} className="btn-secondary" style={{width:"100%"}}>Cancel</button>
            </div>
          </div>
            </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
